import { useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import { Form, message } from "antd";
import {
  useStockTransfersQuery,
  useActiveStoresQuery,
  useProductsQuery,
  useCreateStockTransferMutation,
} from "../../../Services";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../../Constant";
import { TransferItem, StockTransfer, Store, Product } from "../../../Types";

/**
 * Hook to retrieve and manage the paginated list of stock transfers.
 */
export function useStockTransfersList(
  page: number,
  search: string,
  fromStoreId: string | number | null,
  toStoreId: string | number | null,
) {
  const { data, isLoading, refetch } = useStockTransfersQuery({
    page,
    limit: PAGE_SIZE,
    search,
    from_store_id: fromStoreId ? String(fromStoreId) : null,
    to_store_id: toStoreId ? String(toStoreId) : null,
  });

  return {
    transfers: data || [],
    total: data?.total || 0,
    isLoading,
    refetch,
  };
}

/**
 * Hook to fetch active stores list and parse the API response array.
 */
export function useActiveStores() {
  const { data: activeStoresResponse, isLoading } = useActiveStoresQuery();
  const stores: Store[] = Array.isArray(activeStoresResponse)
    ? activeStoresResponse
    : ((activeStoresResponse as any)?.stores ?? []);

  return { stores, isLoading };
}

/**
 * Hook to search and look up products available in a specific source store.
 */
export function useStoreProductsLookup(storeId: string | number | null) {
  const [productSearch, setProductSearch] = useState("");
  const [debouncedSearch] = useDebounce(productSearch, DEBOUNCED_SEARCH_QUERY);

  const { data, isLoading } = useProductsQuery(
    {
      page: 1,
      limit: 100,
      search: debouncedSearch,
      store_id: storeId ? String(storeId) : null,
    },
    { enabled: !!storeId },
  );

  const products = data?.data ?? [];

  return { products, setProductSearch, isLoading };
}

/**
 * Hook to wrap the stock transfer creation mutation.
 */
export function useCreateStockTransfer(onSuccess: () => void) {
  const createMutation = useCreateStockTransferMutation();

  const originalMutate = createMutation.mutate;
  const mutate = (payload: {
    from_store_id: string;
    to_store_id: string;
    note?: string;
    items: Array<{
      product_id: string;
      stock_id: string;
      quantity: number;
    }>;
  }) => {
    originalMutate(payload, {
      onSuccess: (res) => {
        if (res.success) {
          message.success(
            res.message || "Stock transfer completed successfully!",
          );
          onSuccess();
        } else {
          message.error(res.message || "Failed to execute stock transfer.");
        }
      },
      onError: (err: any) => {
        message.error(
          err?.message ||
            "An error occurred while creating the stock transfer.",
        );
      },
    });
  };

  return {
    ...createMutation,
    mutate,
  };
}

/**
 * Consolidated hook representing the state machines and business logic for the Stock Transfer page.
 */
export function useStockTransfer() {
  // --- Filtering & Paginated List States ---
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);

  const [filterFromStore, setFilterFromStore] = useState<string | null>(null);
  const [filterToStore, setFilterToStore] = useState<string | null>(null);

  // --- Modal / Drawer View States ---
  const [newDrawerOpen, setNewDrawerOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] =
    useState<StockTransfer | null>(null);

  // --- Form & Selected Store Listeners ---
  const [form] = Form.useForm();
  const fromStoreId = Form.useWatch("from_store_id", form);

  // --- Current Picker States (Items to be transferred) ---
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [selectedStockId, setSelectedStockId] = useState<string | null>(null);
  const [transferQty, setTransferQty] = useState<number>(1);
  const [transferItems, setTransferItems] = useState<TransferItem[]>([]);

  // --- Data Queries ---
  const { transfers, total, isLoading, refetch } = useStockTransfersList(
    page,
    search,
    filterFromStore,
    filterToStore,
  );
  const { stores } = useActiveStores();
  const {
    products,
    setProductSearch,
    isLoading: isProductsLoading,
  } = useStoreProductsLookup(fromStoreId);

  // Reset selected items when source store changes
  useEffect(() => {
    setTransferItems([]);
    setSelectedProductId(null);
    setSelectedStockId(null);
  }, [fromStoreId]);

  const handleCloseNewDrawer = () => {
    form.resetFields();
    setTransferItems([]);
    setSelectedProductId(null);
    setSelectedStockId(null);
    setTransferQty(1);
    setNewDrawerOpen(false);
  };

  const createMutation = useCreateStockTransfer(handleCloseNewDrawer);

  // Selected item computed values from lookup list
  const selectedProduct = products.find((p) => p.id === selectedProductId);
  const selectedStock = selectedProduct?.stocks?.find(
    (s) => s.id === selectedStockId,
  );

  const handleProductChange = (id: string) => {
    setSelectedProductId(id);
    setSelectedStockId(null);
  };

  const handleAddTransferItem = () => {
    if (!selectedProductId || !selectedStockId || transferQty <= 0) {
      message.error(
        "Please select product, stock batch, and enter a valid quantity.",
      );
      return;
    }

    if (!selectedProduct || !selectedStock) return;

    if (
      !selectedProduct.is_negative_qty_allowed &&
      selectedStock.quantity < transferQty
    ) {
      message.error(
        `Insufficient quantity. Max available: ${selectedStock.quantity}`,
      );
      return;
    }

    const existingIndex = transferItems.findIndex(
      (item) => item.stock_id === selectedStockId,
    );

    if (existingIndex > -1) {
      const updated = [...transferItems];
      const newQty = updated[existingIndex].quantity + transferQty;
      if (
        !selectedProduct.is_negative_qty_allowed &&
        selectedStock.quantity < newQty
      ) {
        message.error(
          "Cannot add more. Combined quantity exceeds batch stock.",
        );
        return;
      }
      updated[existingIndex].quantity = newQty;
      setTransferItems(updated);
    } else {
      setTransferItems([
        ...transferItems,
        {
          product_id: selectedProductId,
          product_title: selectedProduct.title,
          sku_code: selectedProduct.sku_code,
          unit: selectedProduct.unit,
          stock_id: selectedStockId,
          batch_no: selectedStock.batch_no,
          quantity: transferQty,
          available_qty: selectedStock.quantity,
        },
      ]);
    }

    // Reset item picker options
    setSelectedProductId(null);
    setSelectedStockId(null);
    setTransferQty(1);
  };

  const handleRemoveTransferItem = (stockId: string) => {
    setTransferItems((prev) =>
      prev.filter((item) => item.stock_id !== stockId),
    );
  };

  const handleCreateTransferSubmit = async () => {
    if (transferItems.length === 0) {
      message.error("Please add at least one item to transfer.");
      return;
    }

    try {
      const values = await form.validateFields();
      createMutation.mutate({
        from_store_id: values.from_store_id,
        to_store_id: values.to_store_id,
        note: values.note,
        items: transferItems.map((item) => ({
          product_id: item.product_id,
          stock_id: item.stock_id,
          quantity: item.quantity,
        })),
      });
    } catch {
      // Form validation error messages displayed inline
    }
  };

  return {
    page,
    setPage,
    searchInput,
    setSearchInput,
    filterFromStore,
    setFilterFromStore,
    filterToStore,
    setFilterToStore,
    newDrawerOpen,
    setNewDrawerOpen,
    selectedTransfer,
    setSelectedTransfer,
    form,
    fromStoreId,
    selectedProductId,
    selectedStockId,
    transferQty,
    transferItems,
    transfers,
    total,
    isLoading,
    refetch,
    stores,
    products,
    setProductSearch,
    isProductsLoading,
    selectedProduct,
    selectedStock,
    isSubmitting: createMutation.isPending,
    handleCloseNewDrawer,
    handleProductChange,
    handleAddTransferItem,
    handleRemoveTransferItem,
    handleCreateTransferSubmit,
    setSelectedStockId,
    setTransferQty,
  };
}
